import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DeepPartial } from '@reduxjs/toolkit';
import { format, parseISO } from 'date-fns';
import { useLazyGetCachedProfileQuery } from 'features/api/userSlice';
import { selectSaldoPrelevabile } from 'features/dashboard/selectors';
import { useTypedSelector } from 'lib/centralStore';
import { getNonce } from 'lib/policy';
import { Session } from 'next-auth';
import { useRouter } from 'next/router';
import { breakpoint, isServerSide } from 'utility/constant';
import { formatAmount, getAmount, isMobileBrowser } from 'utility/functions';
import { useInternalSession } from 'hooks/useInternalSession';
import { getLngFromCultureName } from 'hooks/useLingUI';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    DigitalAssistant: any;
    DA: any;
    dataLayer: any;
    fbq: any;
  }
}

type UseWebchatPayload = {
  condition: boolean;
  session?: DeepPartial<Session>;
  onInit: Function;
};
type UseWebchatResponse = {
  onOpen: () => void;
};
// eslint-disable-next-line no-unused-vars
type UseWebchat = (payload: UseWebchatPayload) => UseWebchatResponse;
type UseWebchatExtended = () => UseWebchatResponse;

const useWebchat: UseWebchat = ({ condition, onInit }) => {
  const [script, setScript] = useState<HTMLScriptElement>();

  const onError = useCallback(async (err) => {
    console.log('error loading script', err);
  }, []);

  const onInitDigitalAssistant = useCallback(async () => {
    if (isServerSide) {
      return;
    }

    const initParams = await onInit();
    if (!!initParams) {
      window?.DigitalAssistant.init(initParams);
    }
  }, [onInit]);

  const createScript = useCallback(() => {
    if (isServerSide) {
      return;
    }

    const script = document.createElement('script');

    script.async = true;
    script.id = 'chatbot';
    script.nonce = getNonce();
    script.src = process.env.NEXT_PUBLIC_DIGITAL_ASSISTANT_URL || '';
    script.onerror = onError;
    script.onload = onInitDigitalAssistant;

    setScript(script);
  }, [onError, onInitDigitalAssistant]);

  const onOpen = useCallback<() => void>(() => {
    if (isServerSide) {
      return;
    } else if (
      !!window.DigitalAssistant &&
      window.DigitalAssistant.statusInit === window.DigitalAssistant.StatusInit.INITIALIZED
    ) {
      window.DigitalAssistant.show();
    } else if (condition) {
      createScript();
    }
  }, [condition, createScript]);

  useEffect(() => {
    if (isServerSide || !script || !condition) {
      return;
    }

    document.body.appendChild(script);

    return () => {
      window.DigitalAssistant?.hide();
      window.DigitalAssistant?.disconnect();
      delete window.DigitalAssistant;
      delete window.DA;

      document.body.removeChild(script);
    };
  }, [condition, script]);

  return {
    onOpen,
  };
};

export const useWebchatLogin: UseWebchatExtended = () => {
  const { locale } = useRouter();
  const { isAuthenticated, session } = useInternalSession();
  const [getSessionCacheProfile] = useLazyGetCachedProfileQuery();

  const { doCheck } = useWindowSize();
  const isMobile = doCheck([{ comparer: enComparer.less | enComparer.equal, size: breakpoint.sm }]);

  const saldoPrelevabile = useTypedSelector(selectSaldoPrelevabile);

  const onInit = useCallback(async () => {
    try {
      if (isServerSide) {
        throw new Error();
      }

      const profileData = await getSessionCacheProfile().unwrap();
      const {
        nome,
        club,
        codfisc,
        cognome,
        cartaceo,
        cod_prom,
        flag_wagering,
        data_creazione_contratto,
        tot_versamenti,
        num_versamento,
        active_cu,
        flag_marketing,
        telefono_cell,
        last_login_date,
        data_scad_doc,
        flag_profilazione,
        data_primo_versamento,
        saldo_carta,
        saldo_wagering,
        impo_primo_vers,
      } = profileData;

      const origin = `${process.env.NEXT_PUBLIC_CHAT_SOURCE ?? 'preview'}${isMobileBrowser() ? '-mobile' : ''}`;

      window.DigitalAssistant.UserInfo.username = session?.user?.name; //'<username>';
      window.DigitalAssistant.UserInfo.displayname = nome + '' + cognome; //"<nome cognome>";
      window.DigitalAssistant.UserInfo.notes = 'dashboard'; //'<nome sezione sito, Esempio: sport>';

      const personal_params = {
        user_language: getLngFromCultureName(locale),
        wsc_account_code: session?.user?.contractCode,
        chat_source: origin,
        showDocumentValidityAlert: true,
        hasUserAuthorizedChatPrivacy: 1,
        club: club,
        cod_prom: cod_prom,
        data_scad_doc: data_scad_doc
          ? data_scad_doc.length === 8
            ? format(
                new Date(
                  `${data_scad_doc.substring(0, 4)}-${data_scad_doc.substring(4, 6)}-${data_scad_doc.substring(6, 8)}`
                ),
                'dd/MM/yyyy'
              )
            : data_scad_doc
          : '',
        data_primo_versamento: data_primo_versamento,
        codfisc: codfisc,
        nome: nome,
        cognome: cognome,
        flag_marketing: flag_marketing === '1' ? true : false,
        flag_profilazione: flag_profilazione === '1' ? true : false,
        cartaceo: cartaceo ? +cartaceo : 0,
        last_login_date: last_login_date,
        data_crea_contr: data_creazione_contratto ? format(parseISO(data_creazione_contratto), 'dd/MM/yyyy') : null,
        num_versamento: num_versamento ? +num_versamento : 0,
        datanasc: session?.user?.datanasc && format(parseISO(session?.user?.datanasc), 'dd/MM/yyyy'),
        active_cu: !!active_cu,
        flag_wagering: flag_wagering ? !!+flag_wagering : false,
        tot_versamenti: tot_versamenti ? formatAmount(getAmount(tot_versamenti)) : formatAmount(0),
        e_mail: session?.user?.email,
        telefono_mobile: telefono_cell,
        saldo_carta: formatAmount(getAmount(saldo_carta)),
        saldo_wagering: formatAmount(getAmount(saldo_wagering)),
        saldo_prelevabile: saldoPrelevabile ? formatAmount(getAmount(saldoPrelevabile)) : formatAmount(0),
        impo_primo_vers: impo_primo_vers ? formatAmount(getAmount(impo_primo_vers)) : formatAmount(0),
      };

      const engagentFormErrorTopic = 'REG_FORM_EVT';

      return {
        cid: process.env.NEXT_PUBLIC_DIGITAL_ASSISTANT_CID, //'205d106b17323bdccede4dca5681a4c994b54562', // id identificativo SNAI
        key:
          typeof window?.engagentFormErrorCode !== 'undefined' && typeof window?.engagentFormErrorMsg !== 'undefined'
            ? engagentFormErrorTopic
            : undefined,
        sessionId: 'wsc_account_code',
        baseUrl: process.env.NEXT_PUBLIC_CHAT_SERVICE_URL,
        view: isMobile ? { mobileMode: 'detect', status: 'visible' } : { status: 'visible' },
        params: personal_params,
      };
    } catch (error) {
      console.log('error init digital assistant');
      return null;
    }
  }, [
    locale,
    isMobile,
    saldoPrelevabile,
    session?.user?.name,
    session?.user?.email,
    session?.user?.datanasc,
    session?.user?.contractCode,
    getSessionCacheProfile,
  ]);

  return useWebchat({ condition: isAuthenticated, session, onInit });
};

export const useWebchatAnonymius: UseWebchatExtended = () => {
  const { doCheck } = useWindowSize();

  const isMobile = doCheck([{ comparer: enComparer.less | enComparer.equal, size: breakpoint.sm }]);

  const { pathname, locale } = useRouter();

  const condition = useMemo<boolean>(() => /registrati/.test(pathname), [pathname]);

  const onInit = useCallback(async () => {
    try {
      if (isServerSide) {
        throw new Error();
      }

      //Entered User Data on Landingpage form
      //GTAG
      if (window.dataLayer) {
        window.dataLayer.push('event', 'sent', {
          event_category: 'form',
        });
      }

      //FACEBOOK PIXEL
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'Lead');
      }

      window.DigitalAssistant.UserInfo.username = 'Usr anonimo area registrazione';

      let origin = `${process.env.NEXT_PUBLIC_CHAT_SOURCE ?? 'preview'}${isMobileBrowser() ? '-mobile' : ''}`;

      var engagentFormErrorTopic = 'REG_FORM_EVT';
      var engagentFormError = [
        engagentFormErrorTopic,
        window?.engagentFormErrorCode,
        window?.engagentFormErrorMsg,
      ].join('_');

      return {
        cid: process.env.NEXT_PUBLIC_DIGITAL_ASSISTANT_CID,
        key:
          typeof window?.engagentFormErrorCode !== 'undefined' && typeof window?.engagentFormErrorMsg !== 'undefined'
            ? engagentFormErrorTopic
            : undefined,
        sessionId: 'wsc_account_code',
        view: isMobile ? { mobileMode: 'detect', status: 'visible' } : { status: 'visible' },
        baseUrl: process.env.NEXT_PUBLIC_CHAT_SERVICE_URL,
        params: {
          user_language: getLngFromCultureName(locale),
          wsc_account_code: '',
          chat_source: origin,
          registration_form_error:
            typeof window?.engagentFormErrorCode !== 'undefined' && typeof window?.engagentFormErrorMsg !== 'undefined'
              ? engagentFormError
              : undefined,
          showDocumentValidityAlert: true,
        },
      };

      //DIGITAL ASSISTANT
    } catch (error) {
      console.log('error init digital assistant');
      return null;
    }
  }, [isMobile, locale]);

  return useWebchat({ condition, onInit });
};